import { useCallback, useEffect, useRef, useState } from "react";

import { ApiClientError } from "./api";

export interface AsyncState<T> {
  data: T | null;
  error: ApiClientError | Error | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Minimal data hook.
 *
 * ⚠️ It exposes `error` as a first-class state, and pages MUST render it. Code that reads
 * a new relation may degrade gracefully to an empty state during a migration window, but
 * it must **throw on genuine errors** rather than silently render zeros — a dashboard
 * showing "0 generations" because the API is down is worse than one showing an error,
 * because the reader believes it.
 */
export function useApi<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiClientError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Guards against a slow first response overwriting a fast second one.
  const latest = useRef(0);

  const run = useCallback(() => {
    const seq = ++latest.current;
    setLoading(true);
    setError(null);
    fn()
      .then((result) => {
        if (seq === latest.current) setData(result);
      })
      .catch((err: unknown) => {
        if (seq === latest.current) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (seq === latest.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(run, [run, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
